import React, { useState } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { Link } from 'react-router';
import { useMutation } from '@apollo/client';
import { LOG_IN } from '../../graphql/mutations/mutations';
import { Form, Input, Button, message, Typography, Divider } from 'antd';
import { LoginFormValues } from '../../types/auth';
import { AuthWithSocial } from './AuthWithSocial';
import { IUser } from '../../types/user';
import { STORAGE_KEY } from '../../providers/RoleProvider';
import { useAuthLayoutContext } from '../../layouts/AuthLayout';
import TagManager from 'react-gtm-module';

const { Title, Text } = Typography;

export const Login: React.FC = () => {
  const { setAuthUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const emailValue = Form.useWatch('email', form);
  const { isMobile, setMobileView } = useAuthLayoutContext();

  const onError = (error: { message?: string }) => {
    message.error(error.message || 'Login failed');
    setLoading(false);
  };

  const onCompleted = (data: IUser) => {
    if (data?.id) {
      try {
        localStorage.removeItem(`${STORAGE_KEY}_${data.id}`);
      } catch (error) {
        console.error(error);
      }
      setAuthUser(data);
      message.success('Login successful!');
    } else {
      message.error((data as { message?: string }).message || 'Login failed');
    }
    setLoading(false);
  };

  const [login] = useMutation(LOG_IN, {
    onCompleted: data => {
      onCompleted(data?.LogIn);
    },
    onError,
  });

  const handleNext = async () => {
    try {
      await form.validateFields(['email']);
      TagManager.dataLayer({
        dataLayer: {
          event: 'login_next',
          page: '/login',
          step: 'email',
        },
      });
      setStep(2);
    } catch {
      // validation errors shown by Ant Design
    }
  };

  const handleFinish = () => {
    if (step === 1) {
      handleNext();
      return;
    }
    const allValues = form.getFieldsValue(true);
    TagManager.dataLayer({
      dataLayer: {
        event: 'login_submit',
        page: '/login',
      },
    });
    setLoading(true);
    login({
      variables: {
        email: allValues.email,
        password: allValues.password,
      },
    });
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (isMobile) {
      setMobileView('landing');
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ email: '', password: '' }}>
      {step === 1 ? (
        <>
          <Title level={3}>Sign in</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            to start your ADU project
          </Text>

          <AuthWithSocial
            onComplete={setAuthUser}
            onError={onError}
            onLoading={setLoading}
            successMessage="Login successful!"
            errorMessage="Login failed"
            loading={loading}
            type="login"
          />

          <Divider plain>or continue with email</Divider>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}>
            <Input placeholder="your@email.com" size="large" />
          </Form.Item>

          <Button
            type="primary"
            size="large"
            block
            onClick={handleNext}
            disabled={!emailValue?.trim()}>
            Next
          </Button>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text>
              Don&apos;t have an account? <Link to="/register">Register</Link>
            </Text>
          </div>

          {isMobile && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Button type="link" onClick={() => setMobileView('landing')}>
                Back
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <Title level={3}>Enter your password</Title>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter your password' }]}>
            <Input.Password placeholder="Your password" size="large" />
          </Form.Item>

          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Link
              to="/forgot-password"
              onClick={() =>
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'login_forgot_password',
                    page: '/login',
                  },
                })
              }>
              Forgot password?
            </Link>
          </div>

          <Button type="primary" htmlType="submit" size="large" block loading={loading}>
            Login
          </Button>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button type="link" onClick={handleBack}>
              Back
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
